import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import ContaReceberForm from 'app/main/contareceber/components/ContaReceberForm';
import { cancelarAlert } from 'app/services/alerts';
import conciliacaoApi from 'app/services/api/conciliacaoApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { FaArrowCircleUp } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { ConciliacaoContext } from '../ConciliacaoContext';
import { ConciliacaoModal } from './ConciliacaoModal';

const MOMENT_HOJE = moment();

const CadastroContaReceberModal = ({ origemConciliacao }) => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useContext(ConciliacaoContext).modal;
  const [conciliacao, setConciliacao] = useContext(ConciliacaoContext).conciliacao;
  const [conciliacoes, setConciliacoes] = useContext(ConciliacaoContext).conciliacoes;
  const [contaReceber, setContaReceber] = useContext(ConciliacaoContext).contaReceber;

  const CONCILIACAO = 'Conciliação';

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useReduxLoading(loading);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const close = () => {
    setConciliacao({});
    setContaReceber({});
    formik.resetForm(getInitialValues(contaReceber));
    setModal('');
  };

  const handleCloseModal = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  const getInitialValues = (contaReceber) => ({
    ...contaReceber,
    mesCompetencia: contaReceber?.mesCompetencia ? contaReceber.mesCompetencia : MOMENT_HOJE,
    baixar: !!contaReceber?.baixa,
    cliente: contaReceber?.cliente
      ? {
          id: contaReceber.cliente.id,
          nome: contaReceber.cliente?.pessoa?.nome || contaReceber.cliente?.nome,
          pessoaId: contaReceber.cliente?.pessoa?.id || contaReceber.cliente?.pessoaId,
        }
      : null,
    categorias: contaReceber.categorias || [{}],
    tipo: 'UNICA',
  });

  const montarObjetoContaReceber = (contaReceber) => {
    const categorias = [];

    for (let i = 0; i < contaReceber?.categorias.length; i++) {
      categorias.push({
        classeId: contaReceber?.categorias[i].classe?.id,
        valorBruto: contaReceber?.categorias[i].valorBruto,
        classe: {
          id: contaReceber?.categorias[i].classe?.id,
          indice: contaReceber?.categorias[i].classe?.indice,
          nome: contaReceber?.categorias[i].classe?.nome,
          natureza: contaReceber?.categorias[i].classe?.natureza,
          nivel: contaReceber?.categorias[i].classe?.nivel,
        },
        rateios: contaReceber?.categorias[i].rateios,
      });
    }

    return {
      clientePessoaId: contaReceber?.cliente?.pessoaId,
      contaId: contaReceber?.conta?.id,
      dataVencimento: contaReceber?.dataVencimento,
      tipo: contaReceber?.tipo,
      mesCompetencia: contaReceber?.mesCompetencia,
      documentoFiscal: contaReceber?.documentoFiscal,
      nossoNumeroAntigo: contaReceber?.nossoNumeroAntigo,
      categorias: categorias,
      observacoes: contaReceber?.observacoes,
      id: contaReceber.id,
    };
  };

  const onSubmitForm = async (contaReceber) => {
    try {
      setLoading(true);
      if (contaReceber.id) {
        let conta = montarObjetoContaReceber(contaReceber);

        eventReactGA(CONCILIACAO, 'Atualizar conta a receber');
        const retorno = await conciliacaoApi.editarContaReceber(conciliacao.id, conta);

        setLoading(false);
        aplicarDadosAtualizados(retorno.data);
        close();
        showSuccess('Conta a receber editada com sucesso.');
      } else {
        let conta = montarObjetoContaReceber(contaReceber);

        eventReactGA(CONCILIACAO, 'Adicionar conta a receber');
        const retorno = await conciliacaoApi.cadastrarContaReceber(conciliacao.id, conta);

        setLoading(false);
        aplicarDadosAtualizados(retorno.data);
        close();
        showSuccess('Conta a receber salva com sucesso.');
      }
    } catch (err) {
      setLoading(false);
      apiUtils.handleApiError(err);
    }
  };

  const alteraReceitas = (receitas, contaReceber) => {
    let isEdicao = receitas?.some((receita) => receita.id === contaReceber.id);

    if (isEdicao) {
      receitas = receitas.map((conta) => {
        if (conta.id === contaReceber.id) {
          return montaEstruturaDoLancamento(contaReceber);
        }
        return conta;
      });
    } else {
      receitas === undefined && (receitas = []);
      receitas.push(montaEstruturaDoLancamento(contaReceber));
    }
    return receitas.filter((receita) => receita.cliente);
  };

  const montaEstruturaDoLancamento = (contaReceber) => {
    return {
      id: contaReceber?.id,
      cliente: { pessoa: { nome: contaReceber.cliente?.pessoa?.nome } },
      baixa: contaReceber?.baixa,
      valor: contaReceber?.situacao === 'BAIXADA' ? contaReceber?.baixa.valorPago : contaReceber?.valorBruto,
      categorias: contaReceber?.categorias,
      formulario: false,
    };
  };

  const aplicarDadosAtualizados = (contaReceber) => {
    let listaAtualizada = conciliacoes.map((conciliacaoAux) => {
      if (conciliacaoAux.id === conciliacao.id) {
        conciliacaoAux.contaReceberList = alteraReceitas(conciliacaoAux.contaReceberList, contaReceber);
      }

      return conciliacaoAux;
    });

    setConciliacoes(listaAtualizada);
  };

  const formik = useFormik({
    initialValues: getInitialValues(contaReceber),
    validationSchema: yup.object().shape({
      mesCompetencia: yup
        .string()
        .nullable()
        .required(mensagemCampoObrigatorio('a competência')),
      cliente: yup
        .object()
        .nullable()
        .required(mensagemCampoObrigatorio('o cliente')),
      categorias: yup.array().of(
        yup.object().shape({
          id: yup.string().nullable(),
          classe: yup
            .object()
            .nullable()
            .required(mensagemCampoObrigatorio('a categoria'))
            .test('classe', mensagemCampoObrigatorio('a categoria'), function(classe) {
              return !!classe && !!classe?.id;
            }),
          valorBruto: yup
            .number()
            .nullable()
            .min(0.01, 'O valor deve ser maior que zero.')
            .required(mensagemCampoObrigatorio('o valor bruto')),
        })
      ),
      dataVencimento: yup
        .date()
        .nullable()
        .required(mensagemCampoObrigatorio('a data de vencimento')),
      conta: yup
        .object()
        .nullable()
        .test('contaNotNull', mensagemCampoObrigatorio('a conta'), function(conta) {
          if (this.parent?.baixar || !!this.parent?.nossoNumero || !!this.parent?.nossoNumeroAntigo) {
            return !!conta?.id;
          }
          return true;
        }),
      baixa: yup
        .object()
        .nullable()
        .shape({
          dataRecebimento: yup
            .date()
            .nullable()
            .test('dataRecebimentoNotNull', mensagemCampoObrigatorio('a data de recebimento'), function(
              dataRecebimento
            ) {
              if (this.parent.baixar) {
                return !!dataRecebimento;
              }
              return true;
            }),
        }),
      recorrencia: yup.object().when('repetirLancamento', {
        is: true,
        then: yup.object().shape({
          frequencia: yup
            .string()
            .nullable()
            .required(mensagemCampoObrigatorio('a frequência')),
          quantidade: yup
            .number()
            .nullable()
            .required(mensagemCampoObrigatorio('a quantidade')),
        }),
      }),
    }),
    onSubmit: onSubmitForm,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const { resetForm } = formik;
  useEffect(() => {
    resetForm(getInitialValues(contaReceber));
  }, [contaReceber, resetForm]);

  return (
    <SimpleDialog fullScreen open={modal === ConciliacaoModal.cadastroContaReceberModal} onClose={handleCloseModal}>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={formik.handleSubmit}
          fullScreen
          header={{
            title: 'Lançamento de contas a receber',
            icon: <FaArrowCircleUp />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleCloseModal}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="submit">
                  Salvar
                </SaveButton>
              </>
            ),
          }}
          content={
            <div style={{ margin: '15px 0px' }}>
              <ContaReceberForm origemConciliacao={origemConciliacao} />
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default CadastroContaReceberModal;
